import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

import { closeForm } from "../feature/formSlice";
import { callAPI } from "../feature/APISlice";
import { showMessage } from "../feature/navbarSlice";
import { fetchLookups } from "../feature/lookupSlice";

const BASE_URL = `${process.env.REACT_APP_PRODUCTION_API_URL}backend/api/admin/events`;

const getDefaultFormData = () => ({
  title: "",
  shortDescription: "",
  longDescription: "",
  orderNumber: "",
  eventDate: "",

  coreService: "",

  bannerImage: "",
  bannerVideo: "",
  mobileBannerImage: "",
  mobileBannerVideo: "",
  relatedMedia: [],

  instagramUrl: "",
  youTubeUrl: "",
  faceBookUrl: "",

  eventAddress: "",
  addressGoogleMapUrl: "",

  is_active: false,
});

export default function EventsForm() {
  const dispatch = useDispatch();

  const { isOpen, editingId } = useSelector((s) => s.form);
  const { current, loading, error } = useSelector((s) => s.api);
  const { media, coreServices } = useSelector((s) => s.lookups);

  const [formData, setFormData] = useState(getDefaultFormData());

  useEffect(() => {
    if (isOpen) dispatch(fetchLookups());
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (editingId) {
      dispatch(callAPI({ method: "get", url: `${BASE_URL}/${editingId}` }));
    }
  }, [editingId, dispatch]);

  useEffect(() => {
    if (current && editingId) {
      setFormData({
        title: current.title ?? "",
        shortDescription: current.shortDescription ?? "",
        longDescription: current.longDescription ?? "",
        orderNumber: current.orderNumber ?? "",
        eventDate: current.eventDate
          ? current.eventDate.split("T")[0]
          : "",

        coreService: current.coreService?.id ?? "",

        bannerImage: current.bannerImage?.rivid ?? "",
        bannerVideo: current.bannerVideo?.rivid ?? "",
        mobileBannerImage: current.mobileBannerImage?.rivid ?? "",
        mobileBannerVideo: current.mobileBannerVideo?.rivid ?? "",
        relatedMedia: current.relatedMedia?.map((m) => m.rivid) ?? [],

        instagramUrl: current.instagramUrl ?? "",
        youTubeUrl: current.youTubeUrl ?? "",
        faceBookUrl: current.faceBookUrl ?? "",

        eventAddress: current.eventAddress ?? "",
        addressGoogleMapUrl: current.addressGoogleMapUrl ?? "",

        is_active: current.is_active ?? false,
      });
    }

    if (!editingId) setFormData(getDefaultFormData());
  }, [current, editingId]);

  const mediaOptions = useMemo(
    () =>
      media.map((m) => ({
        value: m.rivid,
        label: `RIVID: ${m.rivid}`,
      })),
    [media]
  );

  const coreServiceOptions = useMemo(
    () =>
      coreServices.map((c) => ({
        value: c.id,
        label: c.title,
      })),
    [coreServices]
  );

  const handleChange = (name, value) =>
    setFormData((p) => ({ ...p, [name]: value }));

  const handleClose = () => {
    dispatch(closeForm());
    setFormData(getDefaultFormData());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingId ? "patch" : "post";
      const url = editingId ? `${BASE_URL}/${editingId}` : BASE_URL;

      await dispatch(callAPI({ method, url, data: formData })).unwrap();

      dispatch(
        showMessage({
          type: "success",
          text: `Event ${editingId ? "updated" : "created"} successfully`,
        })
      );

      handleClose();
    } catch (err) {
      dispatch(showMessage({ type: "error", text: err || "Something went wrong" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-form-overlay">
      <div className="admin-form-wrapper" onClick={(e) => e.stopPropagation()}>
        <button className="admin-form-close" onClick={handleClose}>✕</button>

        <h2>{editingId ? "Edit Event" : "Add Event"}</h2>

        <form className="admin-contact-form" onSubmit={handleSubmit}>

          
          <div className="admin-form-group">
            <label>Title</label>
            <input value={formData.title} onChange={(e) => handleChange("title", e.target.value)} />
          </div>

          
          <div className="admin-form-group">
            <label>Short Description</label>
            <textarea rows={2} value={formData.shortDescription} onChange={(e) => handleChange("shortDescription", e.target.value)} />
          </div>

          <div className="admin-form-group">
            <label>Long Description</label>
            <textarea rows={4} value={formData.longDescription} onChange={(e) => handleChange("longDescription", e.target.value)} />
          </div>

          
          <div className="admin-form-group">
            <label>Order Number</label>
            <input value={formData.orderNumber} onChange={(e) => handleChange("orderNumber", e.target.value)} />
          </div>

          <div className="admin-form-group">
            <label>Event Date</label>
            <input type="date" value={formData.eventDate} onChange={(e) => handleChange("eventDate", e.target.value)} />
          </div>

          
          <div className="admin-form-group">
            <label>Core Service</label>
            <Select
              options={coreServiceOptions}
              value={coreServiceOptions.find(o => o.value === formData.coreService) || null}
              onChange={(o) => handleChange("coreService", o?.value || "")}
              isClearable
            />
          </div>

          
          {[
            ["bannerImage", "Desktop Banner Image"],
            ["bannerVideo", "Desktop Banner Video"],
            ["mobileBannerImage", "Mobile Banner Image"],
            ["mobileBannerVideo", "Mobile Banner Video"],
          ].map(([key, label]) => (
            <div className="admin-form-group" key={key}>
              <label>{label} (Media RIVID)</label>
              <Select
                options={mediaOptions}
                value={mediaOptions.find(o => o.value === Number(formData[key])) || null}
                onChange={(o) => handleChange(key, o?.value || "")}
                isClearable
              />
            </div>
          ))}

          
          <div className="admin-form-group">
            <label>Related Media</label>
            <Select
              options={mediaOptions}
              isMulti
              value={mediaOptions.filter(o => formData.relatedMedia.includes(o.value))}
              onChange={(opts) =>
                handleChange("relatedMedia", opts ? opts.map(o => o.value) : [])
              }
            />
          </div>


          {["instagramUrl", "youTubeUrl", "faceBookUrl"].map((f) => (
            <div className="admin-form-group" key={f}>
              <label>{f}</label>
              <input value={formData[f]} onChange={(e) => handleChange(f, e.target.value)} />
            </div>
          ))}


          <div className="admin-form-group">
            <label>Event Address</label>
            <input value={formData.eventAddress} onChange={(e) => handleChange("eventAddress", e.target.value)} />
          </div>

          <div className="admin-form-group">
            <label>Google Map URL</label>
            <input value={formData.addressGoogleMapUrl} onChange={(e) => handleChange("addressGoogleMapUrl", e.target.value)} />
          </div>

          <label>
            <input type="checkbox" checked={formData.is_active} onChange={(e) => handleChange("is_active", e.target.checked)} /> Active
          </label>

          <div className="admin-button-row">
            <button type="button" className="admin-btn outline" onClick={handleClose}>Cancel</button>
            <button type="submit" className="admin-btn filled" disabled={loading}>
              {loading ? "Saving..." : editingId ? "Update" : "Save"}
            </button>
          </div>

          {error && <p className="error-text">{error}</p>}
        </form>
      </div>
    </div>
  );
}
